import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { ListChecks, Plus, Lock, Trash2, Pencil, Check, X, AlertTriangle } from "lucide-react";
import { fmtEuro } from "@/lib/format";

function UncategorizedSection({ canEdit }) {
  const qc = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());
  const [pick, setPick] = useState({});
  const { data } = useQuery({ queryKey: ["erp-suppliers", year], queryFn: async () => (await api.get(`/erp/suppliers?year=${year}`)).data });
  const { data: lines = [] } = useQuery({ queryKey: ["erp-pl-lines"], queryFn: async () => (await api.get("/erp/pl-lines")).data });
  const save = useMutation({
    mutationFn: async ({ supplier, category_code }) => (await api.put("/erp/suppliers/map", { supplier, category_code })).data,
    onSuccess: (_d, v) => { toast.success(`«${v.supplier}» → ${v.category_code}`); ["erp-suppliers", "pl-report", "facts", "data-health"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const rows = (data?.rows || []).filter((r) => (r.pl_code === "999" || !r.pl_code) && !r.excluded);
  const total = rows.reduce((s, r) => s + (r.included || 0), 0);
  const opts = lines.filter((l) => l.code !== "999");
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-3" data-testid="uncategorized-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-medium text-[#45423C] inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[#C86235]" /> Αταξινόμητα έξοδα (999) · {fmtEuro(total)}</h2>
          <p className="text-xs text-[#6F6A60] mt-0.5">Προμηθευτές SoftOne χωρίς λογαριασμό P&L. Διαλέξτε λογαριασμό και πατήστε ✓ — το P&L και το καθολικό ξαναχτίζονται αυτόματα.</p>
        </div>
        <select data-testid="uncat-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm">
          {[0, 1, 2].map((d) => { const y = new Date().getFullYear() - d; return <option key={y} value={y}>{y}</option>; })}
        </select>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#3F6B4C] bg-[#E2ECE4] rounded-xl px-3 py-2" data-testid="uncat-empty">Όλα τα έξοδα του {year} είναι ταξινομημένα.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]"><tr><th className="px-3 py-2 text-left">Προμηθευτής</th><th className="px-3 text-left">Κατηγορία SoftOne</th><th className="px-3 text-right">Παραστατικά</th><th className="px-3 text-right">Ποσό</th><th className="px-3 text-left">Λογαριασμός P&L</th><th className="px-3" /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.supplier} className="border-t border-[#E3DED4]" data-testid={`uncat-row-${r.supplier}`}>
                  <td className="px-3 py-2 font-medium text-[#45423C]">{r.supplier}</td>
                  <td className="px-3 text-[#6F6A60] text-xs">{(r.softone_categories || []).join(", ") || "—"}</td>
                  <td className="px-3 text-right tabular">{r.docs}</td>
                  <td className="px-3 text-right tabular">{fmtEuro(r.included)}</td>
                  <td className="px-3">
                    <select data-testid={`uncat-pick-${r.supplier}`} disabled={!canEdit} value={pick[r.supplier] || ""} onChange={(e) => setPick({ ...pick, [r.supplier]: e.target.value })} className="rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm max-w-[280px]">
                      <option value="">— επιλέξτε —</option>
                      {opts.map((l) => <option key={l.code} value={l.code}>{l.code} · {l.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`uncat-save-${r.supplier}`} disabled={!canEdit || !pick[r.supplier] || save.isPending} onClick={() => save.mutate({ supplier: r.supplier, category_code: pick[r.supplier] })}
                      className="p-1.5 rounded-lg bg-[#45423C] text-white disabled:opacity-40"><Check className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const inp = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C] min-w-0";
const btn = "inline-flex items-center gap-2 rounded-xl bg-[#45423C] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2F2D29] disabled:opacity-50";
const card = "rounded-2xl bg-white border border-[#E3DED4] overflow-hidden";
const head = "px-4 py-3 bg-[#F3F0EA] text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold";

const useRefresh = () => {
  const qc = useQueryClient();
  return () => ["finance-accounts", "custom-accounts", "expense-lines", "income-budget", "expense-budget-monthly", "pl-own"]
    .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
};

const IncomeSection = ({ meta, canEdit }) => {
  const refresh = useRefresh();
  const { data: customs = [] } = useQuery({ queryKey: ["custom-accounts"], queryFn: async () => (await api.get("/finance/custom-accounts")).data });
  const [f, setF] = useState({ name: "", cost_center: "SHARED", keywords: "" });
  const body = { name: f.name, section: "revenue", cost_center: f.cost_center, keywords: f.keywords.split(",").map((k) => k.trim()).filter(Boolean) };
  const add = useMutation({
    mutationFn: async () => (await api.post("/finance/custom-accounts", body)).data,
    onSuccess: (d) => { refresh(); setF({ name: "", cost_center: "SHARED", keywords: "" }); toast.success(`Νέα κατηγορία εσόδων ${d.code} ${d.name}`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const del = useMutation({
    mutationFn: async (code) => api.delete(`/finance/custom-accounts/${code}`),
    onSuccess: () => { refresh(); toast.success("Διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const byCode = Object.fromEntries(customs.map((c) => [c.code, c]));
  const rows = (meta?.accounts || []).filter((a) => a.section === "revenue" && a.code !== "4210");
  return (
    <div className={card} data-testid="cat-income-section">
      <div className={head}>Κατηγορίες εσόδων</div>
      {canEdit && (
        <div className="p-4 grid gap-2 sm:grid-cols-[2fr_1fr_2fr_auto] border-b border-[#E3DED4]">
          <input data-testid="inc-cat-name" className={inp} placeholder="Όνομα (π.χ. Έσοδα Yoga)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <select data-testid="inc-cat-cc" className={inp} value={f.cost_center} onChange={(e) => setF({ ...f, cost_center: e.target.value })}>
            {(meta?.cost_centers || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <input data-testid="inc-cat-keywords" className={inp} placeholder="Λέξεις υπηρεσίας Hotelizer (π.χ. YOGA, BOAT)" value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} />
          <button data-testid="inc-cat-add" className={btn} disabled={!f.name.trim() || add.isPending} onClick={() => add.mutate()}><Plus className="h-4 w-4" /> Προσθήκη</button>
        </div>
      )}
      <div className="overflow-x-auto"><table className="w-full text-sm"><tbody>
        {rows.map((a) => (
          <tr key={a.code} className="border-t border-[#E3DED4]" data-testid={`inc-cat-row-${a.code}`}>
            <td className="px-4 py-2.5 tabular text-[#6F6A60] w-16">{a.code}</td>
            <td className="px-4 py-2.5 font-medium text-[#45423C]">{a.code === "4200" ? "Έσοδα F&B (φαγητό + ποτά)" : a.name}</td>
            <td className="px-4 py-2.5 text-[#6F6A60] hidden sm:table-cell">{byCode[a.code]?.keywords?.join(", ")}</td>
            <td className="px-4 py-2.5 text-right">
              {byCode[a.code] ? canEdit && (
                <button data-testid={`inc-cat-delete-${a.code}`} className="p-1.5 rounded-lg hover:bg-[#FBEAE3]"
                  onClick={() => window.confirm(`Διαγραφή ${a.name}; Θα διαγραφεί και ο προϋπολογισμός της.`) && del.mutate(a.code)}>
                  <Trash2 className="h-4 w-4 text-[#C86235]" />
                </button>
              ) : <Lock className="inline h-3.5 w-3.5 text-[#B5AFA3]" />}
            </td>
          </tr>
        ))}
      </tbody></table></div>
    </div>
  );
};

const LineEditor = ({ value, onChange, accounts, centers, testid }) => (
  <>
    <input data-testid={`${testid}-label`} className={inp} placeholder="Όνομα (π.χ. Yoga δάσκαλοι)" value={value.label} disabled={value.locked} onChange={(e) => onChange({ ...value, label: e.target.value })} />
    <select data-testid={`${testid}-account`} className={inp} value={value.account_code} onChange={(e) => onChange({ ...value, account_code: e.target.value })}>
      <option value="">— Λογαριασμός προϋπολογισμού —</option>
      {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
    </select>
    <select data-testid={`${testid}-cc`} className={inp} value={value.cost_center} onChange={(e) => onChange({ ...value, cost_center: e.target.value })}>
      {centers.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
    </select>
    <label className="inline-flex items-center gap-2 text-sm text-[#45423C] whitespace-nowrap">
      <input data-testid={`${testid}-below`} type="checkbox" checked={value.below} disabled={value.locked} onChange={(e) => onChange({ ...value, below: e.target.checked })} /> Κάτω από EBITDA
    </label>
  </>
);

const ExpenseSection = ({ meta, canEdit }) => {
  const refresh = useRefresh();
  const { data: lines = [] } = useQuery({ queryKey: ["expense-lines"], queryFn: async () => (await api.get("/finance/expense-lines")).data });
  const EMPTY = { label: "", account_code: "", cost_center: "SHARED", below: false };
  const [f, setF] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const accounts = (meta?.accounts || []).filter((a) => a.section !== "revenue");
  const centers = meta?.cost_centers || [];
  const accName = Object.fromEntries(accounts.map((a) => [a.code, a.name]));
  const add = useMutation({
    mutationFn: async () => (await api.post("/finance/expense-lines", f)).data,
    onSuccess: (d) => { refresh(); setF(EMPTY); toast.success(`Νέα κατηγορία εξόδων ${d.code} ${d.label}`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const save = useMutation({
    mutationFn: async () => (await api.put(`/finance/expense-lines/${edit.code}`, edit)).data,
    onSuccess: () => { refresh(); setEdit(null); toast.success("Αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const del = useMutation({
    mutationFn: async (code) => api.delete(`/finance/expense-lines/${code}`),
    onSuccess: () => { refresh(); toast.success("Διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className={card} data-testid="cat-expense-section">
      <div className={head}>Κατηγορίες εξόδων — ανά λογαριασμό προϋπολογισμού</div>
      {canEdit && (
        <div className="p-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-[2fr_2fr_1fr_auto_auto] items-center border-b border-[#E3DED4]">
          <LineEditor value={f} onChange={setF} accounts={accounts} centers={centers} testid="exp-cat-new" />
          <button data-testid="exp-cat-add" className={btn} disabled={!f.label.trim() || !f.account_code || add.isPending} onClick={() => add.mutate()}><Plus className="h-4 w-4" /> Προσθήκη</button>
        </div>
      )}
      <div className="overflow-x-auto"><table className="w-full text-sm"><tbody>
        {lines.map((l) => edit?.code === l.code ? (
          <tr key={l.code} className="border-t border-[#E3DED4] bg-[#F7F5F1]" data-testid={`exp-cat-edit-row-${l.code}`}>
            <td className="px-4 py-2 tabular text-[#6F6A60]">{l.code}</td>
            <td colSpan={3} className="px-4 py-2"><div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 items-center"><LineEditor value={edit} onChange={setEdit} accounts={accounts} centers={centers} testid="exp-cat-edit" /></div></td>
            <td className="px-4 py-2 text-right whitespace-nowrap">
              <button data-testid="exp-cat-edit-save" onClick={() => save.mutate()} className="p-1.5 rounded-lg hover:bg-white"><Check className="h-4 w-4 text-[#6E7A63]" /></button>
              <button data-testid="exp-cat-edit-cancel" onClick={() => setEdit(null)} className="p-1.5 rounded-lg hover:bg-white"><X className="h-4 w-4" /></button>
            </td>
          </tr>
        ) : (
          <tr key={l.code} className="border-t border-[#E3DED4]" data-testid={`exp-cat-row-${l.code}`}>
            <td className="px-4 py-2.5 tabular text-[#6F6A60] w-16">{l.code}</td>
            <td className="px-4 py-2.5 font-medium text-[#45423C]">{l.label}{l.custom && <span className="ml-2 rounded-full bg-[#EEF1EA] px-2 py-0.5 text-[10px] text-[#6E7A63]">δική σας</span>}</td>
            <td className="px-4 py-2.5 text-[#6F6A60]">{l.account_code ? `${l.account_code} ${accName[l.account_code] || ""}` : <span className="text-[#C86235]">χωρίς λογαριασμό</span>}</td>
            <td className="px-4 py-2.5 text-[#6F6A60] hidden md:table-cell">{l.below ? "Κάτω από EBITDA" : "Λειτουργικά"}</td>
            <td className="px-4 py-2.5 text-right whitespace-nowrap">
              {canEdit && <button data-testid={`exp-cat-edit-${l.code}`} onClick={() => setEdit({ ...l, locked: !l.custom })} className="p-1.5 rounded-lg hover:bg-[#F3F0EA]"><Pencil className="h-4 w-4 text-[#45423C]" /></button>}
              {canEdit && l.custom && (
                <button data-testid={`exp-cat-delete-${l.code}`} className="p-1.5 rounded-lg hover:bg-[#FBEAE3]"
                  onClick={() => window.confirm(`Διαγραφή ${l.label}; Θα διαγραφεί και ο προϋπολογισμός της.`) && del.mutate(l.code)}>
                  <Trash2 className="h-4 w-4 text-[#C86235]" />
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody></table></div>
    </div>
  );
};

export default function FinanceCategories() {
  const { user } = useAuth();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const { data: meta } = useQuery({ queryKey: ["finance-accounts"], queryFn: async () => (await api.get("/finance/accounts")).data });
  return (
    <div className="space-y-6" data-testid="finance-categories-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-[#45423C] flex items-center gap-3"><ListChecks className="h-7 w-7 text-[#C86235]" /> Κατηγορίες Εσόδων & Εξόδων</h1>
        <p className="text-sm text-[#6F6A60] mt-2 max-w-3xl">Μία λίστα για Προϋπολογισμό και P&L. Κάθε κατηγορία εμφανίζεται με το ίδιο όνομα στο «Έσοδα/Έξοδα: Budget vs Πραγματικά» και στο «Αποτελέσματα P&L». Τα έξοδα ομαδοποιούνται κάτω από τον λογαριασμό προϋπολογισμού τους. Νέα κατηγορία εξόδων: αντιστοιχίστε της προμηθευτές από τη λίστα παραστατικών κάτω από το P&L. Νέα κατηγορία εσόδων: τα παραστατικά Hotelizer με αυτές τις λέξεις μπαίνουν εκεί μετά την «Ανανέωση παραστατικών».</p>
      </div>
      <UncategorizedSection canEdit={canEdit} />
      <IncomeSection meta={meta} canEdit={canEdit} />
      <ExpenseSection meta={meta} canEdit={canEdit} />
    </div>
  );
}
